import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Metric } from "@/components/Metric";
import DataTable from "@/components/DataTable";
import StatusBadge from "@/components/StatusBadge";
import { ChartCard, BarSeries, LineSeries, PieSeries, EmptyChart } from "@/components/Charts";
import { fmtCurrency, fmtDate } from "@/lib/format";

export default function Overview() {
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    api.get("/platform/dashboard").then((r) => setData(r.data));
    api.get("/platform/analytics").then((r) => setAnalytics(r.data));
  }, []);

  if (!data) return <div className="overline text-stone-500">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="platform-overview">
      <PageHeader
        eyebrow="Platform Console"
        title="Operations overview"
        description="Snapshot of tenant companies, subscriptions, and manual billing activity."
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric label="Companies" value={data.total_companies} sub={`${data.active_companies} active`} accent testId="metric-companies" />
        <Metric label="Active Subs" value={data.active_subscriptions} sub={`${data.total_subscriptions} total`} testId="metric-subs" />
        <Metric label="Plans" value={data.total_plans} sub="Available to assign" testId="metric-plans" />
        <Metric label="Revenue (manual)" value={fmtCurrency(data.total_revenue)} sub={`${data.expiring_soon} expiring < 30d`} testId="metric-revenue" />
      </div>

      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="platform-charts">
          <ChartCard title="Revenue by Month" subtitle="Manual subscription payments (INR)" accent testId="chart-revenue">
            {analytics.revenue_by_month?.length > 0 ? (
              <BarSeries data={analytics.revenue_by_month} yKey="amount" yLabel="Revenue" />
            ) : (
              <EmptyChart />
            )}
          </ChartCard>
          <ChartCard title="Companies Onboarded" subtitle="New tenants per month" testId="chart-companies">
            {analytics.companies_by_month?.length > 0 ? (
              <LineSeries data={analytics.companies_by_month} yKey="count" yLabel="Companies" />
            ) : (
              <EmptyChart />
            )}
          </ChartCard>
          <ChartCard title="Active Subscriptions by Plan" subtitle="Distribution across tiers" testId="chart-subs-by-plan">
            {analytics.subs_by_plan?.length > 0 ? (
              <PieSeries data={analytics.subs_by_plan} nameKey="plan_name" valueKey="count" />
            ) : (
              <EmptyChart />
            )}
          </ChartCard>
        </div>
      )}

      <div>
        <div className="overline text-stone-500 mb-3">Recently added companies</div>
        <DataTable
          testId="recent-companies-table"
          columns={[
            { key: "name", label: "Company", render: (r) => <div className="font-bold">{r.name}</div> },
            { key: "email", label: "Email" },
            { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
            { key: "created_at", label: "Created", render: (r) => fmtDate(r.created_at) },
          ]}
          rows={data.recent_companies || []}
          empty="No companies onboarded yet."
        />
      </div>
    </div>
  );
}
